import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Alert, Box, CircularProgress, Stack, Tab, Tabs, Typography } from "@mui/material";
import { Briefcase, AlertTriangle, CalendarClock, Timer, Eye, PenLine, History } from "lucide-react";
import { CardHeader, EmptyState, GlassCard, PageHeader, StatRow, StatTile, T, TYPE, formatDate, formatRelative } from "../user/complaints/shared";
import { useSocketEvent } from "../helpdesk/socket";
import { pm, errorMessage } from "./api";
import { PmPage, usePm } from "./PmContext";
import { TypeIcon, PriorityIcon, StatusPill, Points, ACTIVITY_TEXT } from "./ui";
import PmTopBar from "./PmTopBar";
import IssueDetail, { IssueDetailModal } from "./IssueDetail";
import IssueNavigator from "./IssueNavigator";

const IssueList = ({ rows, onOpen, projects, empty }) => {
  const key = (i) => projects.find((p) => String(p._id) === String(i.projectId))?.key || i.key.split("-")[0];
  if (!rows?.length) {
    return (
      <Box sx={{ py: 6, textAlign: "center", px: 2 }}>
        <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>{empty}</Typography>
      </Box>
    );
  }
  return rows.map((i) => (
    <Stack
      key={i._id}
      direction="row"
      spacing={1.25}
      alignItems="center"
      onClick={() => onOpen(i)}
      sx={{
        px: 2,
        py: 1.25,
        borderBottom: `1px solid ${T.border}`,
        cursor: "pointer",
        transition: "background-color 0.15s ease",
        "&:hover": { bgcolor: "rgba(99, 102, 241, 0.04)" },
      }}
    >
      <TypeIcon typeId={i.typeId} size={15} />
      <Typography sx={{ fontSize: TYPE.small, fontWeight: 700, color: T.brand, minWidth: 85 }}>{i.key}</Typography>
      <Typography noWrap sx={{ fontSize: TYPE.small, fontWeight: 500, color: T.ink, flexGrow: 1 }}>{i.summary}</Typography>
      <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, display: { xs: "none", md: "block" }, bgcolor: T.track, px: 0.8, py: 0.2, borderRadius: "4px" }}>
        {key(i)}
      </Typography>
      {i.dueDate && (
        <Typography sx={{ fontSize: TYPE.micro, fontWeight: 600, color: new Date(i.dueDate) < new Date() && i.statusCategory !== "done" ? "#B42318" : T.inkMuted }}>
          {formatDate(i.dueDate)}
        </Typography>
      )}
      <Points value={i.storyPoints} />
      <PriorityIcon priorityId={i.priorityId} size={16} />
      <StatusPill statusId={i.statusId} />
    </Stack>
  ));
};

const Inner = ({ mode }) => {
  const ctx = usePm();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("assigned");
  const [open, setOpen] = useState(null);
  const load = useCallback(() => pm.myWork().then(setData).catch((err) => setError(errorMessage(err))), []);
  useEffect(() => {
    load();
  }, [load]);
  useSocketEvent(["notification.created"], () => load());

  if (mode === "issues") {
    return (
      <>
        <PmTopBar />
        <PageHeader icon={Briefcase} title="My Issues" subtitle="Search, filter, and track all issues and tickets across workspace projects" />
        <IssueNavigator />
      </>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Box sx={{ py: 10, textAlign: "center" }}><CircularProgress size={26} /></Box>;
  const lists = { assigned: data.assigned, sprint: data.sprintWork, due: data.dueToday, overdue: data.overdue, watched: data.watched, created: data.created, recent: data.recent };
  return (
    <>
      <PmTopBar onCreated={load} />
      <PageHeader
        icon={Briefcase}
        title="My Work"
        subtitle={`What's on your plate across ${data.projects.length} accessible projects · ${ctx.me.name || "Workspace Member"}`}
      />
      <StatRow columns={5}>
        <StatTile label="Assigned to me" value={data.counts.assigned} icon={Briefcase} onClick={() => setTab("assigned")} />
        <StatTile label="Active sprint" value={data.sprintWork.length} icon={Timer} tone="info" onClick={() => setTab("sprint")} sub={data.activeSprints.map((s) => s.name).slice(0, 2).join(", ")} />
        <StatTile label="Due today" value={data.counts.dueToday} icon={CalendarClock} tone={data.counts.dueToday ? "warning" : "good"} onClick={() => setTab("due")} />
        <StatTile label="Overdue" value={data.counts.overdue} icon={AlertTriangle} tone={data.counts.overdue ? "critical" : "good"} onClick={() => setTab("overdue")} />
        <StatTile label="Watching" value={data.watched.length} icon={Eye} tone="neutral" onClick={() => setTab("watched")} />
      </StatRow>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" }, gap: 2.5 }}>
        <GlassCard sx={{ overflow: "hidden", minWidth: 0 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ px: 1, borderBottom: `1px solid ${T.border}`, minHeight: 44, "& .MuiTab-root": { minHeight: 44, textTransform: "none", fontSize: TYPE.small, fontWeight: 600 } }}>
            <Tab value="assigned" label={`Assigned (${data.assigned.length})`} />
            <Tab value="sprint" label="Current sprint" />
            <Tab value="due" label="Due today" />
            <Tab value="overdue" label="Overdue" />
            <Tab value="watched" label="Watching" />
            <Tab value="created" label="Reported by me" />
            <Tab value="recent" label="Recently viewed" />
          </Tabs>
          <IssueList rows={lists[tab]} onOpen={setOpen} projects={data.projects} empty="No issues found in this category" />
        </GlassCard>
        <Stack spacing={2.5}>
          <GlassCard>
            <CardHeader icon={Timer} title="Active sprints" />
            <Box sx={{ p: 1 }}>
              {!data.activeSprints.length && <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted, p: 1 }}>No active sprints in your projects.</Typography>}
              {data.activeSprints.map((s) => {
                const p = data.projects.find((x) => String(x._id) === String(s.projectId));
                return (
                  <Box key={s._id} onClick={() => p && navigate(`/projects/${p.key}/board`)} sx={{ p: 1, borderRadius: "6px", cursor: "pointer", "&:hover": { bgcolor: T.surfaceMuted } }}>
                    <Typography sx={{ fontSize: TYPE.small, fontWeight: 600 }}>{s.name}</Typography>
                    <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{p?.name || ""}{s.endDate ? ` · ends ${formatDate(s.endDate)}` : ""}</Typography>
                  </Box>
                );
              })}
            </Box>
          </GlassCard>
          <GlassCard>
            <CardHeader icon={History} title="My recent activity" />
            <Box sx={{ p: 2 }}>
              {!data.activity.length && <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>No recent activity.</Typography>}
              {data.activity.slice(0, 12).map((a) => (
                <Box key={a._id} sx={{ py: 0.6 }}>
                  <Typography sx={{ fontSize: TYPE.small }}>{ACTIVITY_TEXT(a)}</Typography>
                  <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{formatRelative(a.createdAt)}</Typography>
                </Box>
              ))}
            </Box>
          </GlassCard>
          {!data.projects.length && (
            <GlassCard><EmptyState dense icon={PenLine} title="No projects yet" description="Create or join a project to see work here." action={<Typography component={RouterLink} to="/projects" sx={{ color: T.brand, fontSize: TYPE.small }}>Go to projects</Typography>} /></GlassCard>
          )}
        </Stack>
      </Box>
      <IssueDetailModal
        issueKey={open?.key}
        open={Boolean(open)}
        onClose={() => setOpen(null)}
        onOpenIssue={(key) => setOpen({ key })}
        onChanged={load}
      />
    </>
  );
};

const MyWork = ({ mode }) => (
  <PmPage>
    <Inner mode={mode} />
  </PmPage>
);

export default MyWork;
